import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { type BoxShopItemFormRef } from "@/modules/boxShop/components/boxShopItemForm"
import type { BoxShopItemEntity } from "@/modules/boxShop/entities/boxShopItemEntity"
import { useBoxShopItems } from "@/modules/boxShop/hooks/useBoxShopItem"
import { useBoxShopSidebar } from "@/modules/boxShop/hooks/useBoxShopSidebar"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@/modules/core/utils/i18n"
import { currencySymbol } from "@/modules/currency/currency"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { HUDatatable } from "@/modules/ui/components/dataTables/huDatatable"
import { HeaderTemplate } from "@/modules/ui/components/dataTables/templates/headerTemplate"
import { createColumnHelper } from "@tanstack/react-table"
import { useEffect, useEffectEvent, useRef, useState } from "react"

export function BoxShopInventoryDatable({
  searchTerm,
  className,
  ...props
}: { searchTerm?: string; className?: string } & React.ComponentProps<"div">) {
  const {
    data: items,
    isLoading,
    pageSize,
    handleSetPage,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasNextPage,
    hasPreviousPage,
    totalElements,
  } = useBoxShopItems({ searchTerm })
  const [selectedItem, setSelectedItem] = useState<BoxShopItemEntity>()
  const { result: currentBox } = useCurrentBox()
  const stripeCurrency = currentBox?.stripeAccountCurrency ?? "EUR"
  const language = useLanguage()
  const canEditItem = useBoxStaffPermission(BoxStaffPermissions.EDIT_ITEM)
  const boxShopItemFormRef = useRef<BoxShopItemFormRef>(null)
  const { navigateToBoxShopItemForm } = useBoxShopSidebar()
  const onSelectedItem = (item: BoxShopItemEntity) => {
    if (!canEditItem) {
      return
    }
    void navigateToBoxShopItemForm({
      boxShopItemFormRef,
      args: {
        replaceAllComponents: true,
        onHide: () => {
          setSelectedItem(undefined)
          return boxShopItemFormRef.current?.onClose() ?? Promise.resolve(true)
        },
      },
      item,
    }).then(() => {
      setSelectedItem(item)
    })
  }

  const columnHelper = createColumnHelper<BoxShopItemEntity>()

  const columns = [
    columnHelper.accessor("title", {
      header: () => <HeaderTemplate title={word("boxShop.item.table.title.header")} />,
      cell: ({ row: { original: item } }) => <span>{item.title}</span>,
    }),
    columnHelper.accessor("stock", {
      header: () => <HeaderTemplate title={word("boxShop.item.table.stock.header")} />,
      cell: ({ row: { original: item } }) => <span>{item.stock}</span>,
    }),
    columnHelper.accessor("price", {
      header: () => (
        <HeaderTemplate
          title={word("boxShop.item.table.price.header", {
            currencySymbol: currencySymbol(stripeCurrency, language),
          })}
        />
      ),
      cell: ({ row: { original: item } }) => <span>{item.price.value}</span>,
    }),
  ]

  const onchangeSearchTerm = useEffectEvent(() => {
    void handleSetPage(0)
  })

  useEffect(() => {
    onchangeSearchTerm()
  }, [searchTerm])

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      <HUDatatable
        data={items}
        columns={columns}
        isLoading={isLoading}
        pageSize={pageSize}
        setPageSize={setPageSize}
        handleRowSelectionChange={(row: BoxShopItemEntity) => onSelectedItem(row)}
        selectedRowIndex={items.findIndex((item) => item.id === selectedItem?.id)}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        totalElements={totalElements}
      />
    </div>
  )
}
